import { useQuery } from "@apollo/client";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, Menu, MenuItem, TextField, Tooltip } from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { Button, Dropdown, Icon } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import { listProductGroups } from "../../../../graphql/queries/listProductGroups";
import { listProducts } from "../../../../graphql/queries/listProducts";
import DeleteConfirmationModal from "../../../common/DeleteConfirmationModal/DeleteConfirmationModal";
import Table from "../../../common/table/Table";
import BoosterStatusTag from "./BoosterStatusTag";
import {
  createEmptyProductMultiplier,
  filterProductMultipliers,
  getProductMultiplierValidationErrors,
} from "./pointsOutcomeUtils";
import TableFilterBar from "./TableFilterBar";
import { TIME_BOUND_FILTER_FIELDS } from "./tableFilterConfig";

const TYPE_DISPLAY = {
  product_group: "Group multiplier",
  product: "Product multiplier",
};
const DEFAULT_FILTERS = { type: "", productId: "", productGroupId: "" };
const TIME_BOUND_FILTERS = {
  ...DEFAULT_FILTERS,
  startDate: "",
  endDate: "",
  status: "",
};
const TYPE_FILTER_OPTIONS = [
  { value: "product", label: "Product multiplier" },
  { value: "product_group", label: "Group multiplier" },
];

/**
 * ProductMultiplierConfig — used in award-purchase-based-points.
 * Provides Default and Time Bounded tabs for product/product-group multipliers.
 */
const ProductMultiplierConfig = ({
  productMultipliers = [],
  onChange,
  onValidationChange,
  showDeleted = false,
}) => {
  const [activeTab, setActiveTab] = useState("default");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [defaultFilters, setDefaultFilters] = useState(DEFAULT_FILTERS);
  const [timeBoundFilters, setTimeBoundFilters] = useState(TIME_BOUND_FILTERS);
  const [touchedRows, setTouchedRows] = useState({});

  // Compute validation errors for all multipliers (only for touched rows)
  const errors = useMemo(() => {
    return productMultipliers.reduce((acc, multiplier) => {
      // Only validate rows that have been touched
      if (!touchedRows[multiplier.id]) {
        return acc;
      }
      const multiplierErrors =
        getProductMultiplierValidationErrors(multiplier, productMultipliers) || {};
      if (Object.keys(multiplierErrors).length > 0) {
        acc[multiplier.id] = multiplierErrors;
      }
      return acc;
    }, {});
  }, [productMultipliers, touchedRows]);

  const hasValidationErrors = Object.keys(errors).length > 0;

  // Propagate validation state to parent
  useEffect(() => {
    onValidationChange?.(hasValidationErrors);
  }, [hasValidationErrors, onValidationChange]);

  const [defaultMenuAnchor, setDefaultMenuAnchor] = useState(null);
  const [timeBoundMenuAnchor, setTimeBoundMenuAnchor] = useState(null);

  const { data: productGroupsData } = useQuery(listProductGroups, {
    variables: { filter: {} },
  });
  const { data: productsData } = useQuery(listProducts, {
    variables: { filter: {}, showDeleted },
  });

  // Object-based options for pepsico-ds Dropdown
  const productGroupOptions = useMemo(
    () =>
      (productGroupsData?.listKeywordGroups || []).map((pg) => ({
        id: pg.id,
        displayText: pg.name,
      })),
    [productGroupsData]
  );

  const productOptions = useMemo(
    () =>
      (productsData?.listKeywordGroups || []).map((p) => ({
        id: p.id,
        displayText: p.name,
      })),
    [productsData]
  );

  const defaultFilterFields = useMemo(
    () => [
      {
        key: "type",
        label: "Type",
        type: "select",
        emptyOptionLabel: "All types",
        options: TYPE_FILTER_OPTIONS,
      },
      {
        key: "productId",
        label: "Product",
        type: "select",
        emptyOptionLabel: "All products",
        options: productOptions.map((product) => ({
          value: product.id,
          label: product.displayText,
        })),
      },
      {
        key: "productGroupId",
        label: "Product Group",
        type: "select",
        emptyOptionLabel: "All product groups",
        options: productGroupOptions.map((group) => ({
          value: group.id,
          label: group.displayText,
        })),
      },
    ],
    [productOptions, productGroupOptions]
  );

  const timeBoundFilterFields = useMemo(
    () => [...defaultFilterFields, ...TIME_BOUND_FILTER_FIELDS],
    [defaultFilterFields]
  );

  const defaultMultipliers = useMemo(
    () => productMultipliers.filter((m) => m.type === "default"),
    [productMultipliers]
  );

  const timeBoundMultipliers = useMemo(
    () => productMultipliers.filter((m) => m.type === "time_bounded"),
    [productMultipliers]
  );

  const filteredDefaultMultipliers = useMemo(
    () => filterProductMultipliers(defaultMultipliers, defaultFilters),
    [defaultMultipliers, defaultFilters]
  );

  const filteredTimeBoundMultipliers = useMemo(
    () => filterProductMultipliers(timeBoundMultipliers, timeBoundFilters),
    [timeBoundMultipliers, timeBoundFilters]
  );

  const updateEntry = useCallback(
    (id, field, value) => {
      // Mark the row as touched when user modifies any field
      setTouchedRows((prev) => ({ ...prev, [id]: true }));
      const updated = productMultipliers.map((m) => {
        if (m.id !== id) return m;
        const entry = { ...m, [field]: value };
        if (field === "level") {
          entry.product_ids = [];
          entry.product_group_ids = [];
        }
        return entry;
      });
      onChange(updated);
    },
    [productMultipliers, onChange]
  );

  const handleAddDefault = useCallback(
    (level) => {
      setDefaultMenuAnchor(null);
      const newEntry = createEmptyProductMultiplier("default", level);
      onChange([...productMultipliers, newEntry]);
    },
    [productMultipliers, onChange]
  );

  const handleAddTimeBound = useCallback(
    (level) => {
      setTimeBoundMenuAnchor(null);
      const newEntry = createEmptyProductMultiplier("time_bounded", level);
      onChange([...productMultipliers, newEntry]);
    },
    [productMultipliers, onChange]
  );

  const handleDelete = useCallback(
    (id) => {
      onChange(productMultipliers.filter((m) => m.id !== id));
      setDeleteTarget(null);
    },
    [productMultipliers, onChange]
  );

  const getItemOptions = (level) =>
    level === "product_group" ? productGroupOptions : productOptions;

  const getItemIds = (entry) =>
    entry.level === "product_group"
      ? entry.product_group_ids || []
      : entry.product_ids || [];

  const getItemField = (level) =>
    level === "product_group" ? "product_group_ids" : "product_ids";

  // Shared render functions
  const renderItemCell = (row) => {
    const options = getItemOptions(row.level);
    const ids = getItemIds(row);
    const selectedValue = options.filter((o) => ids.includes(o.id));
    const placeholder =
      selectedValue.length > 0
        ? `${selectedValue[0].displayText} ${selectedValue.length > 1 ? "(" + (selectedValue.length - 1) + " more)" : ""}`
        : row.level === "product_group"
          ? "Select group"
          : "Select product";
    return (
      <Dropdown
        childList={options}
        label={row.level === "product_group" ? "Product Group" : "Product"}
        selection="multiple"
        selectedValue={selectedValue.length ? selectedValue : undefined}
        placeholder={placeholder}
        size="small"
        setSelectedValue={(val) => {
          const newIds = (val || []).map((v) => v.id);
          updateEntry(row.id, getItemField(row.level), newIds);
        }}
      />
    );
  };

  const renderTypeCell = (row) => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 4,
        fontSize: "0.75rem",
        fontWeight: 500,
        background: row.level === "product_group" ? "#e3f2fd" : "#fff3e0",
        color: row.level === "product_group" ? "#1565c0" : "#e65100",
      }}
    >
      {TYPE_DISPLAY[row.level] || row.level}
    </span>
  );

  const renderValueCell = (row) => (
    <TextField
      size="small"
      type="number"
      placeholder="e.g. 2.0"
      value={row._rawValue ?? ""}
      error={!!errors[row.id]?.value}
      onChange={(e) => {
        const val = e.target.value;
        if (val === "" || Number(val) >= 0) {
          updateEntry(row.id, "value", val);
        }
      }}
      inputProps={{ min: 0 }}
      InputProps={{
        endAdornment: <span style={{ fontSize: "0.8rem", color: "#666" }}>x</span>,
      }}
      style={{ width: 100 }}
    />
  );

  // Default tab columns
  const defaultHeaderData = useMemo(
    () => [
      {
        columnKey: "selected",
        header: "SELECTED",
        render: (row) => renderItemCell(row),
      },
      {
        columnKey: "type",
        header: "TYPE",
        render: (row) => renderTypeCell(row),
      },
      {
        columnKey: "value",
        header: "VALUE",
        render: (row) => renderValueCell(row),
      },
      {
        columnKey: "actions",
        header: "",
        render: (row) => (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteTarget(row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productGroupOptions, productOptions, updateEntry]
  );

  const defaultRowData = useMemo(
    () =>
      filteredDefaultMultipliers.map((m) => ({
        ...m,
        _rawValue: m.value,
        selected:
          getItemIds(m)
            .map(
              (id) => getItemOptions(m.level).find((o) => o.id === id)?.displayText
            )
            .filter(Boolean)
            .join(", ") || "-",
        type: TYPE_DISPLAY[m.level] || m.level,
        value: m.value ? `x${m.value}` : "",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredDefaultMultipliers, productGroupOptions, productOptions]
  );

  // Time-bound tab columns
  const timeBoundHeaderData = useMemo(
    () => [
      {
        columnKey: "selected",
        header: "SELECTED",
        render: (row) => renderItemCell(row),
      },
      {
        columnKey: "type",
        header: "TYPE",
        render: (row) => renderTypeCell(row),
      },
      {
        columnKey: "value",
        header: "VALUE",
        render: (row) => renderValueCell(row),
      },
      {
        columnKey: "start_date",
        header: "START DATE",
        render: (row) => {
          const now = moment();
          return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                value={
                  row.effective_start_date ? moment(row.effective_start_date) : null
                }
                minDateTime={now}
                onAccept={(val) => {
                  updateEntry(
                    row.id,
                    "effective_start_date",
                    val ? val.toISOString() : ""
                  );
                }}
                timeSteps={{ minutes: 1 }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: "small",
                    error:
                      !!errors[row.id]?.startDate || !!errors[row.id]?.dateRange,
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        columnKey: "end_date",
        header: "END DATE",
        render: (row) => {
          const now = moment();
          const minDateTime = row.effective_start_date
            ? moment(row.effective_start_date)
            : now;
          return (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                value={
                  row.effective_end_date ? moment(row.effective_end_date) : null
                }
                minDateTime={minDateTime.isAfter(now) ? minDateTime : now}
                onAccept={(val) => {
                  updateEntry(
                    row.id,
                    "effective_end_date",
                    val ? val.toISOString() : ""
                  );
                }}
                timeSteps={{ minutes: 1 }}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    size: "small",
                    style: { width: 200 },
                    error: !!errors[row.id]?.endDate || !!errors[row.id]?.dateRange,
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        columnKey: "status",
        header: "STATUS",
        render: (row) => (
          <BoosterStatusTag
            startDate={row.effective_start_date}
            endDate={row.effective_end_date}
          />
        ),
      },
      {
        columnKey: "actions",
        header: "",
        render: (row) => (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteTarget(row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productGroupOptions, productOptions, updateEntry, errors]
  );

  const timeBoundRowData = useMemo(
    () =>
      filteredTimeBoundMultipliers.map((m) => ({
        ...m,
        _rawValue: m.value,
        selected:
          getItemIds(m)
            .map(
              (id) => getItemOptions(m.level).find((o) => o.id === id)?.displayText
            )
            .filter(Boolean)
            .join(", ") || "-",
        type: TYPE_DISPLAY[m.level] || m.level,
        value: m.value ? `x${m.value}` : "",
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredTimeBoundMultipliers, productGroupOptions, productOptions]
  );

  const hasDefaultFilters = Object.values(defaultFilters).some(Boolean);
  const hasTimeBoundFilters = Object.values(timeBoundFilters).some(Boolean);

  return (
    <div className="section-card product-multiplier-section">
      <div className="section-header">
        <div>
          <h3>Product and Group Rule Configuration</h3>
          <p className="section-description">
            Set up default multipliers for products and product groups, and
            time-bound multipliers that temporarily override them.
          </p>
        </div>
      </div>

      <div className="config-tabs">
        <button
          className={`config-tab ${activeTab === "default" ? "active" : ""}`}
          onClick={() => setActiveTab("default")}
        >
          Default
        </button>
        <button
          className={`config-tab ${activeTab === "timeBound" ? "active" : ""}`}
          onClick={() => setActiveTab("timeBound")}
        >
          Time Bounded{" "}
          <Icon
            icon="info"
            size="small"
            title="During the validity period, the Time Bounded rule will override the default rule."
          />
        </button>
      </div>

      {activeTab === "default" && (
        <>
          <TableFilterBar
            filters={defaultFilters}
            fields={defaultFilterFields}
            onChange={(key, value) =>
              setDefaultFilters((current) => ({ ...current, [key]: value }))
            }
            onClear={() => setDefaultFilters(DEFAULT_FILTERS)}
          />
          <div className="add-multiplier-row">
            <Button
              size="small"
              variant="primary"
              onClick={(e) => setDefaultMenuAnchor(e.currentTarget)}
              iconTrailing="arrow_drop_down"
            >
              Add Multiplier
            </Button>
            <Menu
              anchorEl={defaultMenuAnchor}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(defaultMenuAnchor)}
              onClose={() => setDefaultMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => handleAddDefault("product_group")}
                sx={{ minWidth: "180px" }}
              >
                Product Group
              </MenuItem>
              <MenuItem
                onClick={() => handleAddDefault("product")}
                sx={{ minWidth: "180px" }}
              >
                Product
              </MenuItem>
            </Menu>
          </div>
          <Table
            headerData={defaultHeaderData}
            rowData={defaultRowData}
            pagination
            rowsPerPage={10}
            noDataLabel={
              hasDefaultFilters
                ? "No product multipliers match the filters"
                : "No product multipliers configured"
            }
          />
          {defaultMultipliers.some((m) => errors[m.id]) && (
            <div className="validation-error">
              {[
                ...new Set(
                  defaultMultipliers.flatMap((m) =>
                    errors[m.id] ? Object.values(errors[m.id]) : []
                  )
                ),
              ].map((msg, idx) => (
                <div key={idx}>
                  <span>&#9888;</span> {msg}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === "timeBound" && (
        <>
          <TableFilterBar
            filters={timeBoundFilters}
            fields={timeBoundFilterFields}
            onChange={(key, value) =>
              setTimeBoundFilters((current) => ({ ...current, [key]: value }))
            }
            onClear={() => setTimeBoundFilters(TIME_BOUND_FILTERS)}
          />
          <div className="add-multiplier-row">
            <Button
              size="small"
              variant="primary"
              onClick={(e) => setTimeBoundMenuAnchor(e.currentTarget)}
              iconTrailing="arrow_drop_down"
            >
              Add Multiplier
            </Button>
            <Menu
              anchorEl={timeBoundMenuAnchor}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={Boolean(timeBoundMenuAnchor)}
              onClose={() => setTimeBoundMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => handleAddTimeBound("product_group")}
                sx={{ minWidth: "180px" }}
              >
                Product Group
              </MenuItem>
              <MenuItem
                onClick={() => handleAddTimeBound("product")}
                sx={{ minWidth: "180px" }}
              >
                Product
              </MenuItem>
            </Menu>
          </div>
          <Table
            headerData={timeBoundHeaderData}
            rowData={timeBoundRowData}
            pagination
            rowsPerPage={10}
            noDataLabel={
              hasTimeBoundFilters
                ? "No time-bounded product multipliers match the filters"
                : "No time-bounded product multipliers configured"
            }
          />
          {timeBoundMultipliers.some((m) => errors[m.id]) && (
            <div className="validation-error">
              {[
                ...new Set(
                  timeBoundMultipliers.flatMap((m) =>
                    errors[m.id] ? Object.values(errors[m.id]) : []
                  )
                ),
              ].map((msg, idx) => (
                <div key={idx}>
                  <span>&#9888;</span> {msg}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        title="Delete Time-Bounded Rule?"
        message="This action cannot be undone. If the rule is currently active, it will stop being applied immediately."
        onConfirm={() => handleDelete(deleteTarget?.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default ProductMultiplierConfig;
